import Action from 'actions';
import {
    assertPlayersSnippetFormatIsValid,
    mapPlayersSnippet,
    parseContentByCells,
    normalizeCsvPlayers,
} from './helpers';
import NumberHelper from 'helpers/number-helper';
import {showPopup, showComponentInPopup} from 'helpers/modal';
import {groupObjectsByProp} from 'helpers/array-helper';
import {getPlayerNameSlug} from 'helpers/player-helper';
import SnippetIsInvalidError from './services/snippet-validators/snippet-is-invalid-error';
import Const from 'const';
import MatchPlayers from 'components/organisms/players-to-match';
import {Match} from 'components/organisms/players-to-match/match';
import {removeRowsWithAllEmptyElements} from 'services/csv/helpers';

function distributeCsvPlayersToMatch(
    csvPlayers: object[],
    playersGroupedByNameSlug: object,
): {matchedCsvPlayers: object[]; notMatchedCsvPlayers: object[]} {
    const csvPlayersGroupedByNameSlug = groupObjectsByProp(csvPlayers, 'nameSlug');
    const matchedCsvPlayers: object[] = [];
    const notMatchedCsvPlayers: object[] = [];

    Object.values(csvPlayersGroupedByNameSlug).map((csvPlayers) => {
        if (csvPlayers.length > 1) {
            csvPlayers.map((csvPlayer) => notMatchedCsvPlayers.push(csvPlayer));
            return;
        }
        // @ts-ignore
        const [csvPlayer] = csvPlayers;
        const {nameSlug} = csvPlayer;

        if (!(nameSlug in playersGroupedByNameSlug) || playersGroupedByNameSlug[nameSlug].length > 1) {
            notMatchedCsvPlayers.push(csvPlayer);
            return;
        }

        matchedCsvPlayers.push(csvPlayer);
    });

    return {
        matchedCsvPlayers,
        notMatchedCsvPlayers,
    };
}

function addNameSlugs(mappedPlayers: object[]): object[] {
    // @ts-ignore
    mappedPlayers.map((player) => (player.nameSlug = getPlayerNameSlug(player.name)));

    const playersGroupedByNameSlug = groupObjectsByProp(mappedPlayers, 'nameSlug');

    const result: any[] = [];
    Object.values(playersGroupedByNameSlug).map((players) => {
        if (players.length > 1) {
            players.map((player, index) => (player.nameSlug += `_${index}`));
        }
        players.map((player) => result.push(player));
    });

    return result;
}

function matchCsvPlayersWithPlayerEntities(csvPlayers: object[], playersGroupedByNameSlug: object): Match[] {
    const result: Match[] = [];

    csvPlayers.map((csvPlayer) => {
        // @ts-ignore
        const {nameSlug = ''} = csvPlayer;
        const player = playersGroupedByNameSlug?.[nameSlug]?.[0] ?? null;

        player && result.push(Match.init(csvPlayer, player.getId()));
    });

    return result;
}

const UploadPlayersCsv = {
    processFileData(fileData) {
        return (dispatch, getState) => {
            const {sportConfig} = getState();
            const parsedContentByCells = parseContentByCells(fileData, sportConfig);
            const normalizedFileContent = removeRowsWithAllEmptyElements(parsedContentByCells);

            dispatch(this.processFilePlayers(normalizedFileContent))
                .then(() => {
                    dispatch(Action.do('toggleDropzone'));

                    return dispatch(showPopup('Changes have been successfully applied.'));
                })
                .catch((error) => {
                    console.error(error);
                    if (error instanceof SnippetIsInvalidError) {
                        return dispatch(showPopup('Incorrect file format.'));
                    }
                })
                .finally(() => {
                    dispatch(Action.do('setDropzoneIsNotLoading'));
                });
        };
    },

    processFilePlayers(parsedContentByCells) {
        return (dispatch, getState) => {
            const {
                sportConfig,
                playersData: {players},
            } = getState();

            try {
                assertPlayersSnippetFormatIsValid(parsedContentByCells, sportConfig);
            } catch (e) {
                return Promise.reject(e);
            }

            let csvPlayers = mapPlayersSnippet(parsedContentByCells, sportConfig);
            csvPlayers = normalizeCsvPlayers(csvPlayers);
            csvPlayers = addNameSlugs(csvPlayers);

            const playersGroupedByNameSlug = groupObjectsByProp(players.getAllAsArr(), 'nameSlug');
            const {matchedCsvPlayers, notMatchedCsvPlayers} = distributeCsvPlayersToMatch(
                csvPlayers,
                playersGroupedByNameSlug,
            );

            return dispatch(this.processNotMatchedPlayers(notMatchedCsvPlayers, matchedCsvPlayers)).then(
                (matches: Match[]) => {
                    matches = [
                        ...matchCsvPlayersWithPlayerEntities(matchedCsvPlayers, playersGroupedByNameSlug),
                        ...matches,
                    ];

                    return dispatch(this.processMatchedPlayers(matches));
                },
            );
        };
    },

    processMatchedPlayers(matches: Match[]) {
        return (dispatch, getState) => {
            if (isEmpty(matches)) {
                return Promise.resolve();
            }

            const {
                playersData: {players, games},
                modesConfig,
                sportConfig,
            } = getState();

            function shouldPlayerBeSkipped(player) {
                return player === null || (sportConfig.isSportMlb() && games.isPlayerGameInactive(player));
            }

            const propsGroupedByMode: object = {};
            const projectedOwnerships: object = {};
            const updatedPlayers: object = {};
            matches.map((match) => {
                const player = players.getPlayer(match.targetPlayerId);

                if (shouldPlayerBeSkipped(player)) {
                    return;
                }
                // @ts-ignore
                const {name, nameSlug, ...playerProps} = match.sourcePlayer;

                Object.keys(playerProps).map((propName) => {
                    // @ts-ignore
                    const value = NumberHelper.float(playerProps[propName], 3, null);

                    if (value === null) {
                        return;
                    }

                    const projOwnPropNames = [
                        'projected_ownership',
                        'captain_projected_ownership',
                        'star_projected_ownership',
                        'pro_projected_ownership',
                    ];

                    if (inArray(propName, projOwnPropNames)) {
                        projectedOwnerships[player.getId()] ??= {};
                        projectedOwnerships[player.getId()][propName] = value;
                    } else if (propName === 'userPoints') {
                        modesConfig.all({asArr: true}).map((modeConfig) => {
                            const role = modeConfig.getRole();
                            const userPoints = NumberHelper.float(modeConfig.getPointCoefficient() * value, 3);
                            const currentPropValue = player.getPropValueOfMode('userPoints', role) * 1;

                            if (userPoints === currentPropValue) {
                                return;
                            }

                            propsGroupedByMode[role] ??= {sympathy: {}, userPoints: []};
                            propsGroupedByMode[role].userPoints.push({playerId: player.getId(), userPoints});
                            propsGroupedByMode[role].sympathy[player.getId()] = null;
                        });
                    } else {
                        player[propName] = value;
                        updatedPlayers[players.doIndex(player.getId())] = player;
                    }
                });
            });

            if (!isEmpty(updatedPlayers)) {
                dispatch(Action.updatePlayersState(updatedPlayers, Const.PLAYERS_MODIFICATION_TYPE_MERGE));
            }

            Object.keys(propsGroupedByMode).map((mode) => {
                dispatch(Action.set('UserPoints', propsGroupedByMode[mode].userPoints, mode, true));
                dispatch(Action.set('PlayersSympathy', {[mode]: propsGroupedByMode[mode].sympathy}));
            });

            if (!isEmpty(projectedOwnerships)) {
                dispatch(Action.set('ProjectedOwnershipsFromCsv', projectedOwnerships));
            }

            return Promise.resolve();
        };
    },

    processNotMatchedPlayers(notMatchedCsvPlayers: object[], matchedCsvPlayers: object[]) {
        return (dispatch) => {
            if (isEmpty(notMatchedCsvPlayers)) {
                return Promise.resolve([]);
            }
            // @ts-ignore
            const matchedPlayerNameSlugs = matchedCsvPlayers.map((csvPlayer) => csvPlayer.nameSlug);

            return new Promise((resolve) => {
                dispatch(
                    showComponentInPopup(
                        MatchPlayers,
                        {
                            csvPlayers: notMatchedCsvPlayers,
                            matchedPlayerNameSlugs,
                            onConfirm: (matches: Match[]) => resolve(matches),
                            onCancel: () => resolve([]),
                        },
                        {
                            useConfirm: false,
                            useCancel: false,
                            additionalClass: 'modal-match',
                        },
                    ),
                );
            });
        };
    },

    processMatchedPlayers1(csvPlayers, playersGroupedByNameSlug) {
        return (dispatch, getState) => {
            if (isEmpty(csvPlayers)) {
                return;
            }

            const {
                playersData: {players},
                modesConfig,
            } = getState();

            const propsGroupedByMode: object = {};
            const projectedOwnerships: object = {};
            const updatedPlayers: object = {};
            csvPlayers.map((csvPlayer) => {
                if (!(csvPlayer.nameSlug in playersGroupedByNameSlug)) {
                    return;
                }

                const {name, nameSlug, ...playerProps} = csvPlayer;
                const [player] = playersGroupedByNameSlug[nameSlug];

                Object.keys(playerProps).map((propName) => {
                    const value = NumberHelper.float(playerProps[propName], 3);
                    const projOwnPropNames = [
                        'projected_ownership',
                        'captain_projected_ownership',
                        'star_projected_ownership',
                        'pro_projected_ownership',
                    ];

                    if (inArray(propName, projOwnPropNames)) {
                        projectedOwnerships[player.getId()] ??= {};
                        projectedOwnerships[player.getId()][propName] = value;
                    } else if (propName === 'userPoints') {
                        modesConfig.all({asArr: true}).map((modeConfig) => {
                            const role = modeConfig.getRole();
                            const userPoints = NumberHelper.float(modeConfig.getPointCoefficient() * value, 3);
                            const currentPropValue = player.getPropValueOfMode('userPoints', role) * 1;

                            if (userPoints === currentPropValue) {
                                return;
                            }

                            propsGroupedByMode[role] ??= {sympathy: {}, userPoints: []};
                            propsGroupedByMode[role].userPoints.push({playerId: player.getId(), userPoints});
                            propsGroupedByMode[role].sympathy[player.getId()] = null;
                        });
                    } else {
                        player[propName] = value;
                        updatedPlayers[players.doIndex(player.getId())] = player;
                    }
                });
            });

            if (!isEmpty(updatedPlayers)) {
                dispatch(Action.updatePlayersState(updatedPlayers, Const.PLAYERS_MODIFICATION_TYPE_MERGE));
            }

            Object.keys(propsGroupedByMode).map((mode) => {
                dispatch(Action.set('UserPoints', propsGroupedByMode[mode].userPoints, mode, true));
                dispatch(Action.set('PlayersSympathy', {[mode]: propsGroupedByMode[mode].sympathy}));
            });

            if (!isEmpty(projectedOwnerships)) {
                dispatch(Action.set('ProjectedOwnershipsFromCsv', projectedOwnerships));
            }
        };
    },

    processFilePlayers1(parsedContentByCells) {
        return (dispatch, getState) => {
            const {
                sportConfig,
                playersData: {players},
                modesConfig,
            } = getState();

            try {
                assertPlayersSnippetFormatIsValid(parsedContentByCells, sportConfig);
            } catch (e) {
                return Promise.reject(e);
            }

            const playersDictionary = mapPlayersSnippet(parsedContentByCells, sportConfig);

            const propsGroupedByMode: object = {};
            const projectedOwnerships: object = {};
            playersDictionary.map((playerDictionary) => {
                // @ts-ignore
                const {playerId, name, ...playerProps} = playerDictionary;

                const player = players.getPlayer(playerId);

                if (player === null) {
                    return;
                }

                Object.keys(playerProps).map((propName) => {
                    const value = NumberHelper.float(playerProps[propName], 3);
                    const projOwnPropNames = [
                        'projected_ownership',
                        'captain_projected_ownership',
                        'star_projected_ownership',
                        'pro_projected_ownership',
                    ];

                    if (inArray(propName, projOwnPropNames)) {
                        player.getId() in projectedOwnerships
                            ? (projectedOwnerships[player.getId()][propName] = value)
                            : (projectedOwnerships[player.getId()] = {[propName]: value});
                    } else if (propName === 'userPoints') {
                        modesConfig.all({asArr: true}).map((modeConfig) => {
                            const role = modeConfig.getRole();
                            const userPoints = NumberHelper.float(modeConfig.getPointCoefficient() * value, 3);
                            const currentPropValue = player.getPropValueOfMode('userPoints', role) * 1;

                            if (userPoints === currentPropValue) {
                                return;
                            }

                            propsGroupedByMode[role] ??= {sympathy: {}, userPoints: []};
                            propsGroupedByMode[role].userPoints.push({playerId: player.getId(), userPoints});
                            propsGroupedByMode[role].sympathy[player.getId()] = null;
                        });
                    } else {
                        player[propName] = value;
                    }
                });
            });

            dispatch(Action.updatePlayersState(players.all()));

            Object.keys(propsGroupedByMode).map((mode) => {
                dispatch(Action.set('UserPoints', propsGroupedByMode[mode].userPoints, mode, true));
                dispatch(Action.set('PlayersSympathy', {[mode]: propsGroupedByMode[mode].sympathy}));
            });

            if (!isEmpty(projectedOwnerships)) {
                dispatch(Action.set('ProjectedOwnershipsFromCsv', projectedOwnerships));
            }

            return Promise.resolve();
        };
    },
};

export default {...Action, ...UploadPlayersCsv};
